/**
 * POSSettings - Configuration POS (auto-déconnexion vendeurs)
 *
 * Permet de configurer :
 * - Activation de la déconnexion automatique
 * - Délai d'inactivité (5-300 secondes)
 *
 * Note : Le vendeur par défaut offline est désormais géré localement
 * sur chaque Mac mini via le bouton 📌 dans la barre des vendeurs.
 */
import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';

export default function POSSettings() {
  const { tenant, reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);

  // États du formulaire
  const [autoLogoutEnabled, setAutoLogoutEnabled] = useState(false);
  const [autoLogoutDelay, setAutoLogoutDelay] = useState(5);
  const [virtualKeyboardEnabled, setVirtualKeyboardEnabled] = useState(false);

  // Charger les settings actuels
  useEffect(() => {
    if (!tenant) return;

    setAutoLogoutEnabled(tenant.pos_auto_logout_enabled ?? false);
    setAutoLogoutDelay(tenant.pos_auto_logout_delay_seconds ?? 5);
    setVirtualKeyboardEnabled(tenant.pos_virtual_keyboard_enabled ?? false);
  }, [tenant]);

  const handleSave = async () => {
    if (!tenant) return;

    setSaving(true);

    try {
      await api.updatePOSSettings(tenant.id, {
        pos_auto_logout_enabled: autoLogoutEnabled,
        pos_auto_logout_delay_seconds: autoLogoutDelay,
        pos_virtual_keyboard_enabled: virtualKeyboardEnabled,
      });

      // Recharger le tenant pour mettre à jour le contexte
      await reloadTenant();

      alert('Paramètres POS sauvegardés avec succès');
    } catch (error) {
      console.error('Failed to save POS settings:', error);
      alert('Erreur lors de la sauvegarde des paramètres POS');
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Paramètres Point de Vente
      </h2>

      <div className="space-y-6">
        {/* Auto-déconnexion */}
        <div className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Déconnexion automatique des vendeurs
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Les vendeurs seront déconnectés automatiquement après une période d'inactivité
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoLogoutEnabled}
                onChange={(e) => setAutoLogoutEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {autoLogoutEnabled && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Délai d'inactivité (en secondes)
              </label>
              <input
                type="number"
                min="5"
                max="300"
                value={autoLogoutDelay}
                onChange={(e) => setAutoLogoutDelay(Number(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-600 mt-2">
                Recommandé : 5-10s pour marché mobile, désactivé pour caisse fixe
              </p>
            </div>
          )}
        </div>

        {/* Clavier virtuel à l'écran */}
        <div className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Clavier virtuel à l'écran
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Affiche un clavier AZERTY tactile lorsqu'un champ de saisie reçoit le focus.
                Désactivez-le si vous utilisez un clavier physique.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Le clavier est déplaçable : faites glisser sa barre de titre pour ne pas masquer les suggestions.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                checked={virtualKeyboardEnabled}
                onChange={(e) => setVirtualKeyboardEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Informations */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-2">
            <span>ℹ️</span>
            <span>À savoir</span>
          </h4>
          <ul className="text-sm text-yellow-800 space-y-1 ml-6 list-disc">
            <li>
              <strong>Mode magasin (caisse fixe) :</strong> Désactivez la déconnexion automatique.
              Chaque caisse a un vendeur attitré qui reste connecté toute la journée.
            </li>
            <li>
              <strong>Mode marché (multi-vendeurs mobile) :</strong> Activez avec un délai de 5-10 secondes.
              Les vendeurs peuvent se déplacer entre les postes et doivent recliquer sur leur nom.
            </li>
            <li>
              Le panier de chaque vendeur est automatiquement sauvegardé lors du changement
              et restauré à la reconnexion.
            </li>
            <li>
              <strong>Mode hors ligne :</strong> En cas de coupure WiFi, tous les vendeurs restent
              cliquables. Les ventes sont enregistrées localement et synchronisées automatiquement
              au retour du réseau.
            </li>
          </ul>
        </div>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
          </button>
        </div>
      </div>
    </div>
  );
}
